import { cn } from "@/modern/utils";
import type { ManifestTagView } from "../utils/useManifestMarketingTags";

export type TagReviewFilter = "all" | "problem" | "solution" | "meta";

export const TAG_TYPE_COLORS: Record<string, string> = {
  problem: "bg-red-500/20 text-red-400",
  solution: "bg-blue-500/20 text-blue-400",
  meta: "bg-violet-500/20 text-violet-400",
};

interface TagReviewListPanelProps {
  tags: ManifestTagView[];
  filteredIndices: number[];
  filter: TagReviewFilter;
  onFilterChange: (f: TagReviewFilter) => void;
  selectedGlobalIndex: number | null;
  onSelect: (globalIndex: number) => void;
}

const FILTER_CONFIG: { key: TagReviewFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "problem", label: "Problems" },
  { key: "solution", label: "Solutions" },
  { key: "meta", label: "Meta" },
];

export const TagReviewListPanel = ({
  tags,
  filteredIndices,
  filter,
  onFilterChange,
  selectedGlobalIndex,
  onSelect,
}: TagReviewListPanelProps) => {
  const counts = {
    all: tags.length,
    problem: tags.filter((t) => t.type === "problem").length,
    solution: tags.filter((t) => t.type === "solution").length,
    meta: tags.filter((t) => t.type === "meta").length,
  };

  return (
    <div className="flex w-[55%] min-w-0 shrink-0 flex-col h-full min-h-0 border-r border-border">
      <div className="px-4 py-3 shrink-0 border-b border-border">
        <div className="flex flex-wrap gap-1">
          {FILTER_CONFIG.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => onFilterChange(key)}
              className={cn(
                "rounded-full px-2.5 py-1 text-[10px] font-medium transition-colors",
                filter === key
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground",
              )}
            >
              {label} ({counts[key]})
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {filteredIndices.length === 0 ? (
          <p className="text-center text-xs text-muted-foreground py-8">No tags in this category.</p>
        ) : (
          filteredIndices.map((globalIdx) => {
            const tag = tags[globalIdx];
            const selected = selectedGlobalIndex === globalIdx;
            return (
              <button
                key={`${globalIdx}-${tag.label}`}
                type="button"
                onClick={() => onSelect(globalIdx)}
                className={cn(
                  "group w-full flex items-start gap-3 px-4 py-3 text-left border-b border-border transition-colors border-l-2",
                  selected
                    ? "bg-primary/[0.08] border-l-primary"
                    : "border-l-transparent hover:bg-accent/30",
                )}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-sm font-medium text-foreground truncate">{tag.label}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">
                    {tag.description || "\u00a0"}
                  </p>
                </div>
                <span
                  className={cn(
                    "shrink-0 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide",
                    TAG_TYPE_COLORS[tag.type] ?? "bg-muted text-muted-foreground",
                  )}
                >
                  {tag.type}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};
